
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import Headers from "../Header";
import Footer from "../Footer";
import ScrollToTop from "../ScrollToTopButton/ScrollToTop"

const PricingPlans = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [countryCode, setCountryCode] = useState(null);

  useEffect(() => {
    // Try to detect user's country for payment method selection
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        setCountryCode(data.country_code);
      })
      .catch((error) => {
        console.error("Error detecting country:", error);
      });
  }, []);

  const plans = [
    {
      name: "Basic",
      description: "For small businesses and startups",
      priceMonthly: 29,
      priceYearly: 290, // 2 months free
      features: [
        "Up to 50 candidates",
        "Standard question templates",
        "Basic analytics",
        "Email support",
      ],
      candidateLimit: 50,
    },
    {
      name: "Pro",
      description: "For growing businesses",
      priceMonthly: 79,
      priceYearly: 790, // 2 months free
      features: [
        "Up to 150 candidates",
        "Advanced question banks",
        "Detailed analytics",
        "Priority email support",
        "Custom branding",
      ],
      candidateLimit: 150,
      popular: true,
    },
    {
      name: "Enterprise",
      description: "For large organizations",
      priceMonthly: 199,
      priceYearly: 1990, // 2 months free
      features: [
        "Up to 500 candidates",
        "All question banks included",
        "Advanced analytics & reporting",
        "24/7 priority support",
        "Custom branding",
        "API access",
        "Dedicated account manager",
      ],
      candidateLimit: 500,
    },
    {
      name: "Custom",
      description: "For unique requirements",
      priceMonthly: null,
      priceYearly: null,
      features: [
        "Custom candidate limit",
        "Fully customizable features",
        "Enterprise integrations",
        "Dedicated support team",
        "Custom development options",
      ],
      candidateLimit: "Custom",
      contact: true,
    },
  ];

  const handleSelectPlan = (plan) => {
    if (plan.contact) {
      navigate("/contact-sales");
    } else {
      navigate("/checkout", {
        state: {
          plan: plan.name.toLowerCase(),
          price: isYearly ? plan.priceYearly : plan.priceMonthly,
          interval: isYearly ? "yearly" : "monthly",
          candidateLimit: plan.candidateLimit,
        },
      });
    }
  };

  return (
    <div className="min-h-screen">
      <Headers />
      {/* Navigation placeholder - would be replaced by your actual Navbar */}
      <div className="h-16 bg-white shadow"></div>
    <div className="container py-10">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Select the plan that best suits your organization's needs. All plans include
          our core features to help you assess candidates effectively.
        </p>
        
        <div className="flex items-center justify-center mt-8 space-x-2">
          <Label htmlFor="billing-toggle" className="text-sm">Monthly</Label>
          <Switch
            id="billing-toggle"
            checked={isYearly}
            onCheckedChange={setIsYearly}
          />
          <Label htmlFor="billing-toggle" className="text-sm">
            Yearly <span className="text-green-600">(Save 16%)</span>
          </Label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <Card
            key={plan.name}
            className={`flex flex-col ${
              plan.popular
                ? "border-primary shadow-lg shadow-primary/10"
                : ""
            }`}
          >
            {plan.popular && (
              <div className="bg-primary text-primary-foreground text-center py-1 px-4 text-sm font-medium">
                MOST POPULAR
              </div>
            )}
            <CardHeader className="pb-8">
              <CardTitle>{plan.name}</CardTitle>
              <CardDescription className="pt-1">
                {plan.description}
              </CardDescription>
              <div className="mt-4">
                {plan.contact ? (
                  <span className="text-3xl font-bold">Contact Sales</span>
                ) : (
                  <>
                    <span className="text-3xl font-bold">
                      ${isYearly ? plan.priceYearly : plan.priceMonthly}
                    </span>
                    <span className="text-muted-foreground ml-1">
                      /{isYearly ? "year" : "month"}
                    </span>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              <ul className="space-y-2">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() => handleSelectPlan(plan)}
                variant={plan.popular ? "default" : "outline"}
                className="w-full"
              >
                {plan.contact ? "Contact Sales" : "Get Started"}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      <div className="text-center mt-16">
        <h2 className="text-2xl font-bold mb-2">Additional Candidates</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
          Need to assess more candidates? You can add additional candidates to any
          plan for just $1.50 per candidate.
        </p>
        <Button onClick={() => navigate("/contact-sales")} variant="outline">
          Contact Us for Volume Pricing
        </Button>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-6 mt-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Need Help Choosing?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
            Our team is ready to help you select the right plan for your organization.
          </p>
          <div className="flex justify-center space-x-4">
            <Button onClick={() => navigate("/contact-sales")} variant="outline">
              Talk to Sales
            </Button>
            <Button onClick={() => navigate("/register")}>
              Start Free Trial
            </Button>
          </div>
        </div>
      </div>
       
    </div>
    {/* Footer placeholder - would be replaced by your actual Footer */}
            <div className="bg-gray-900 text-white py-16">
              <Footer />
            </div>
             <ScrollToTop />
            </div>
  );
};

export default PricingPlans;
